/**
 * WalletContent — web port of the **Account modal** half of
 * `apps/WunderGuide/components/SettingsDrawer.tsx`. RN structure inside
 * `<DrawerOverlay title={ACCOUNT}>`:
 *   AccountBalanceCard → ActivePassesList → AccountActions
 *   (Purchase Credits + Redeem Code) → ActivityHistory → Delete + Sign-out
 *
 * Settings (vision/manifesto/language/notifications/legal) live in
 * `ProfilePanel.tsx` — the two are deliberately separate surfaces in RN.
 */
import React, { useEffect, useState } from 'react';
import billingApi from '../../api/billingApi';
import type { Entitlement, TransactionsResponse } from '../../api/billingApi';
import { useEntitlements } from '../../context/EntitlementsContext';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../hooks/useLanguage';
import AccountBalanceCard from '../billing/AccountBalanceCard';
import ActivityHistory from '../billing/ActivityHistory';
import RedeemCodeModal from '../RedeemCodeModal';
import './WalletContent.css';

type Transaction = TransactionsResponse['transactions'][number];

interface TopupPack {
  id: string;
  label: string;
  seconds: number;
  priceUsd: number;
  badge?: string;
}

const TOPUP_PACKS: TopupPack[] = [
  { id: 'p10', label: '10 minutes', seconds: 600, priceUsd: 4.99 },
  {
    id: 'p30',
    label: '30 minutes',
    seconds: 1800,
    priceUsd: 12.99,
    badge: 'Most popular',
  },
  {
    id: 'p60',
    label: '60 minutes',
    seconds: 3600,
    priceUsd: 19.99,
    badge: 'Best value',
  },
  { id: 'p180', label: '3 hours', seconds: 10800, priceUsd: 49.99 },
];

const WalletContent: React.FC = () => {
  const {
    balance,
    effectiveBalance: _effectiveBalance,
    hasUnmeteredAccess,
    allowance,
    billingEnabled,
    isLowBalance,
    isCriticalBalance,
    boostCredits,
    limitedCredits,
    earliestExpiry,
    refreshBalance,
    startAggressivePolling,
  } = useEntitlements();
  const { logout, deleteAccount } = useAuth();
  const { t } = useLanguage();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [txLoading, setTxLoading] = useState(billingEnabled);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);

  useEffect(() => {
    if (!billingEnabled) {
      setTxLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([
      billingApi.getUserTransactions('voice_seconds', 20, 0).catch(() => null),
      billingApi.getUserEntitlements(true, 'voice_unmetered').catch(() => []),
    ]).then(([txRes, ents]) => {
      if (cancelled) return;
      setTransactions(txRes?.transactions ?? []);
      setEntitlements(ents ?? []);
      setTxLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [billingEnabled]);

  const handleRedeemCode = () => {
    setRedeemModalOpen(true);
  };

  const handleRedeemSuccess = () => {
    // Kick off aggressive polling so the new balance lands within seconds
    // even if the backend takes a moment to commit the credit.
    startAggressivePolling();
  };

  const handleSignOut = () => {
    if (window.confirm('Sign out of your account?')) {
      logout();
    }
  };

  const handleDeleteAccount = () => {
    if (
      window.confirm(
        'Delete your account? This permanently removes your data and cannot be undone.',
      )
    ) {
      deleteAccount();
    }
  };

  return (
    <div className="wg-wallet">
      {/* Balance card — same component & layout as RN AccountBalanceCard */}
      <div className="wg-wallet__balance-row">
        <AccountBalanceCard
          billingEnabled={billingEnabled}
          balance={balance}
          hasUnmeteredAccess={hasUnmeteredAccess}
          isCriticalBalance={isCriticalBalance}
          isLowBalance={isLowBalance}
          allowance={allowance}
          boostCredits={boostCredits ?? undefined}
          limitedCredits={limitedCredits ?? undefined}
          earliestExpiry={earliestExpiry}
        />
        <button
          type="button"
          className="wg-wallet__refresh wg-wallet__refresh--floating"
          onClick={refreshBalance}
          aria-label="Refresh balance"
        >
          <RefreshIcon />
        </button>
      </div>

      {/* Active passes (RN ActivePassesList) */}
      {billingEnabled && entitlements.length > 0 && (
        <div className="wg-wallet__section">
          <div className="wg-wallet__section-title">Active passes</div>
          <div className="wg-wallet__passes">
            {entitlements.map(ent => (
              <div key={ent.entitlement_id} className="wg-wallet__pass">
                <span className="wg-wallet__pass-icon" aria-hidden>
                  <PassIcon />
                </span>
                <span className="wg-wallet__pass-text">
                  <span className="wg-wallet__pass-title">
                    {ent.feature_code === 'voice_unmetered'
                      ? 'Unlimited voice'
                      : ent.feature_code}
                  </span>
                  {ent.ends_at && (
                    <span className="wg-wallet__pass-sub">
                      Until {new Date(ent.ends_at).toLocaleDateString()}
                    </span>
                  )}
                </span>
                <span className="wg-wallet__pass-pill">Active</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top-up packs */}
      <div className="wg-wallet__section">
        <div className="wg-wallet__section-title">Add voice time</div>
        <div className="wg-wallet__packs">
          {TOPUP_PACKS.map(p => (
            <button key={p.id} type="button" className="wg-wallet__pack">
              {p.badge && (
                <span className="wg-wallet__pack-badge">{p.badge}</span>
              )}
              <span className="wg-wallet__pack-label">{p.label}</span>
              <span className="wg-wallet__pack-price">
                ${p.priceUsd.toFixed(2)}
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="wg-wallet__redeem"
          onClick={handleRedeemCode}
        >
          <span className="wg-wallet__redeem-icon" aria-hidden>
            <KeyIcon />
          </span>
          Redeem code
        </button>
      </div>

      {/* Activity history — collapsible, per-row expand, pagination,
          expiry badges, platform labels (Apple/Google/Promo). */}
      {billingEnabled ? (
        <div className="wg-wallet__section">
          <ActivityHistory
            transactions={transactions}
            loading={txLoading}
            initiallyOpen={false}
          />
        </div>
      ) : (
        <div className="wg-wallet__section">
          <div className="wg-wallet__section-title">
            {t('ACTIVITY_HISTORY')}
          </div>
          <div className="wg-wallet__empty">{t('BILLING_NOT_ENABLED')}</div>
        </div>
      )}

      {/* Account-level destructive actions — mirrors RN's renderAccountActions */}
      <div className="wg-wallet__danger">
        <button
          type="button"
          className="wg-wallet__danger-row"
          onClick={handleDeleteAccount}
        >
          <span className="wg-wallet__danger-icon" aria-hidden>
            <TrashIcon />
          </span>
          <span className="wg-wallet__danger-label">Delete account</span>
        </button>
        <button
          type="button"
          className="wg-wallet__danger-row"
          onClick={handleSignOut}
        >
          <span className="wg-wallet__danger-icon" aria-hidden>
            <SignOutIcon />
          </span>
          <span className="wg-wallet__danger-label">Sign out</span>
        </button>
      </div>

      <RedeemCodeModal
        visible={redeemModalOpen}
        onClose={() => setRedeemModalOpen(false)}
        onSuccess={handleRedeemSuccess}
      />
    </div>
  );
};

export default WalletContent;

function RefreshIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
      <path d="M3 22v-6h6" />
      <path d="M21 2v6h-6" />
    </svg>
  );
}
function KeyIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="8" cy="15" r="4" />
      <path d="m10.85 12.15 7.65-7.65" />
      <path d="m18 8 3-3" />
      <path d="m15 5 3 3" />
    </svg>
  );
}
function PassIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z" />
      <path d="M3 12h18" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}
function SignOutIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
